import dayjs from 'dayjs';
import { BODY_MEASURES, BODY_MEASURE_GROUP, BODY_MEASURE_GROUPS, getBodyMeasure } from '../../modules/bodyMeasures';
import { ageAt, computeBodyComposition } from './bodyComposition';

/**
 * Cálculos e formatação do módulo "Medidas corporais". Regras do briefing:
 * medida ausente nunca vira zero (null), variação sempre contra uma referência
 * explícita e sem juízo de "melhor/pior", IMC só com peso e altura.
 * Indicadores calculados (IMC, relações cintura-quadril e cintura-estatura,
 * composição pelas dobras) nunca são gravados: saem das entradas a cada leitura.
 */

export const REFERENCE_PREVIOUS = 'previous';
export const REFERENCE_FIRST = 'first';

export function sortByDateAsc(list) {
  return [...(list || [])].sort((a, b) => {
    const diff = dayjs(a.measuredAt).valueOf() - dayjs(b.measuredAt).valueOf();
    return diff !== 0 ? diff : Number(a.id) - Number(b.id);
  });
}

export function sortByDateDesc(list) {
  return sortByDateAsc(list).reverse();
}

export function formatDate(date) {
  return date ? dayjs(date).format('DD/MM/YYYY') : '';
}

/** Valor de uma medida na avaliação, ou null quando não foi registrada. */
export function getValue(assessment, key) {
  const raw = assessment?.measurements?.[key];
  if (raw === null || raw === undefined || raw === '') return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

export function formatNumber(value, decimals = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return value.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** "82,4 kg" ou "—" quando não há valor. */
export function formatMeasure(value, key) {
  const measure = getBodyMeasure(key);
  const text = formatNumber(value, measure ? measure.decimals : 1);
  if (text === null) return '—';
  return measure ? `${text} ${measure.unit}` : text;
}

/** Diferença entre valor atual e referência; null quando um dos dois falta. */
export function computeDelta(current, reference) {
  if (current === null || reference === null || current === undefined || reference === undefined) return null;
  return Math.round((current - reference) * 100) / 100;
}

/** "+1,5 cm", "-0,8 cm" ou "=" (sem cor de juízo: só direção). */
export function formatDelta(delta, key) {
  if (delta === null || delta === undefined) return null;
  const measure = getBodyMeasure(key);
  const decimals = measure ? measure.decimals : 1;
  if (Math.abs(delta) < Math.pow(10, -decimals) / 2) return '=';
  const sign = delta > 0 ? '+' : '-';
  const text = formatNumber(Math.abs(delta), decimals);
  return measure ? `${sign}${text} ${measure.unit}` : `${sign}${text}`;
}

/**
 * Avaliação de referência para comparar com `current`.
 * `mode`: 'previous' (a imediatamente anterior), 'first' (a mais antiga) ou o id
 * de uma avaliação. Retorna null quando não há com quem comparar.
 */
export function findReference(list, current, mode) {
  if (!current) return null;
  const sorted = sortByDateAsc(list);
  const index = sorted.findIndex((item) => String(item.id) === String(current.id));

  if (mode === REFERENCE_FIRST) {
    const first = sorted[0];
    return first && String(first.id) !== String(current.id) ? first : null;
  }
  if (mode === REFERENCE_PREVIOUS) {
    return index > 0 ? sorted[index - 1] : null;
  }
  const chosen = sorted.find((item) => String(item.id) === String(mode));
  return chosen && String(chosen.id) !== String(current.id) ? chosen : null;
}

/** Chaves que têm valor em pelo menos uma avaliação, na ordem do catálogo. */
export function keysWithData(list) {
  return BODY_MEASURES.map((measure) => measure.key).filter((key) => (list || []).some((item) => getValue(item, key) !== null));
}

/** Valor dentro da faixa plausível do catálogo (`min`/`max`). */
export function isPlausible(key, value) {
  const measure = getBodyMeasure(key);
  if (!measure || value === null || value === undefined) return false;
  return value >= measure.min && value <= measure.max;
}

/**
 * Altura da avaliação ou, se ela não tem, a mais recente registrada antes
 * dela, com a data de onde veio (`heightFrom`). null quando não há nenhuma.
 */
export function resolveHeight(list, current) {
  const height = getValue(current, 'height');
  if (height !== null) return { height, heightFrom: null };

  const sorted = sortByDateAsc(list);
  const index = sorted.findIndex((item) => String(item.id) === String(current.id));
  for (let i = index - 1; i >= 0; i -= 1) {
    const candidate = getValue(sorted[i], 'height');
    if (candidate !== null) return { height: candidate, heightFrom: sorted[i].measuredAt };
  }
  return null;
}

/**
 * IMC = peso / altura², com a altura de `resolveHeight`. Peso ou altura fora
 * da faixa plausível (dado antigo, gravado antes da validação) não viram
 * número: devolve `value: null` com o aviso em `warning`.
 */
export function computeBmi(list, current) {
  const weight = getValue(current, 'weight');
  if (weight === null) return null;

  const resolved = resolveHeight(list, current);
  if (!resolved || resolved.height <= 0) return null;
  const { height, heightFrom } = resolved;

  if (!isPlausible('height', height)) {
    return { value: null, height: null, heightFrom, warning: `Altura de ${formatMeasure(height, 'height')} fora da faixa plausível` };
  }
  if (!isPlausible('weight', weight)) {
    return { value: null, height: null, heightFrom, warning: `Peso de ${formatMeasure(weight, 'weight')} fora da faixa plausível` };
  }

  const meters = height / 100;
  return { value: Math.round((weight / (meters * meters)) * 10) / 10, height, heightFrom };
}

/** Relação cintura-quadril; null sem as duas medidas. */
export function computeWaistHip(assessment) {
  const waist = getValue(assessment, 'waist');
  const hip = getValue(assessment, 'hip');
  if (waist === null || hip === null || !isPlausible('waist', waist) || !isPlausible('hip', hip)) return null;
  return Math.round((waist / hip) * 100) / 100;
}

/** Relação cintura-estatura, com a mesma altura do IMC. */
export function computeWaistHeight(list, assessment) {
  const waist = getValue(assessment, 'waist');
  const resolved = resolveHeight(list, assessment);
  if (waist === null || !resolved || !isPlausible('waist', waist) || !isPlausible('height', resolved.height)) return null;
  return Math.round((waist / resolved.height) * 100) / 100;
}

/**
 * Composição corporal da avaliação pelas dobras e pelo protocolo gravados.
 * `subject`: { variant ('male' | 'female' | 'neutral'), birthday } do paciente;
 * a idade é a da data da avaliação. null quando não há protocolo.
 */
export function computeComposition(assessment, subject) {
  if (!assessment || !assessment.skinfoldProtocol) return null;
  const folds = {};
  BODY_MEASURES
    .filter((measure) => measure.group === BODY_MEASURE_GROUP.SKINFOLDS)
    .forEach((measure) => { folds[measure.key] = getValue(assessment, measure.key); });

  const result = computeBodyComposition({
    protocol: assessment.skinfoldProtocol,
    sex: subject ? subject.variant : null,
    age: subject ? ageAt(subject.birthday, assessment.measuredAt) : null,
    weight: getValue(assessment, 'weight'),
    folds,
  });
  return result.ok ? result : { ...result, reason: describeCompositionGap(result) };
}

/** Texto do que falta para calcular (troca 'missing' pelos nomes das dobras). */
export function describeCompositionGap(result) {
  if (result.reason !== 'missing') return result.reason;
  const names = result.missing.map((key) => getBodyMeasure(key)?.label.toLowerCase()).filter(Boolean);
  return `Faltam as dobras: ${names.join(', ')}.`;
}

/** Chaves dos indicadores calculados, que não estão no catálogo. */
export const BMI_KEY = 'bmi';
export const WAIST_HIP_KEY = 'waist_hip_ratio';
export const WAIST_HEIGHT_KEY = 'waist_height_ratio';
export const SKINFOLD_FAT_KEY = 'skinfold_body_fat';
export const FAT_MASS_KEY = 'fat_mass';
export const LEAN_MASS_KEY = 'lean_mass';

/** Indicadores calculados, na ordem da tabela e do PDF. */
export const COMPUTED_MEASURES = Object.freeze([
  { key: BMI_KEY, label: 'IMC', unit: '', decimals: 1 },
  { key: WAIST_HIP_KEY, label: 'Cintura-quadril', unit: '', decimals: 2 },
  { key: WAIST_HEIGHT_KEY, label: 'Cintura-estatura', unit: '', decimals: 2 },
  { key: SKINFOLD_FAT_KEY, label: 'Gordura pelas dobras', unit: '%', decimals: 1 },
  { key: FAT_MASS_KEY, label: 'Massa gorda', unit: 'kg', decimals: 1 },
  { key: LEAN_MASS_KEY, label: 'Massa magra', unit: 'kg', decimals: 1 },
]);

/** Rótulo, unidade e casas de uma medida do catálogo ou de um indicador calculado. */
export function getMeasureInfo(key) {
  const computed = COMPUTED_MEASURES.find((item) => item.key === key);
  if (computed) return computed;
  const measure = getBodyMeasure(key);
  return measure ? { key, label: measure.label, unit: measure.unit, decimals: measure.decimals } : null;
}

/** "82,4 kg" ou "24,1" (IMC, sem unidade), a partir de `getMeasureInfo`. */
export function formatValue(value, info) {
  const text = formatNumber(value, info.decimals);
  return info.unit ? `${text} ${info.unit}` : text;
}

/** "+1,5 kg", "-0,8" ou "=" em relação ao ponto anterior da série. */
export function formatStep(delta, info) {
  if (delta === null || delta === undefined) return null;
  if (Math.abs(delta) < Math.pow(10, -info.decimals) / 2) return '=';
  const sign = delta > 0 ? '+' : '-';
  const text = formatNumber(Math.abs(delta), info.decimals);
  return info.unit ? `${sign}${text} ${info.unit}` : `${sign}${text}`;
}

/**
 * Valor de uma medida ou de um indicador calculado em uma avaliação, ou null.
 * A composição pelas dobras precisa de `subject` (sexo e nascimento).
 */
export function getMeasureValue(list, assessment, key, subject) {
  switch (key) {
    case BMI_KEY: {
      const bmi = computeBmi(list, assessment);
      return bmi ? bmi.value : null;
    }
    case WAIST_HIP_KEY:
      return computeWaistHip(assessment);
    case WAIST_HEIGHT_KEY:
      return computeWaistHeight(list, assessment);
    case SKINFOLD_FAT_KEY:
    case FAT_MASS_KEY:
    case LEAN_MASS_KEY: {
      const composition = computeComposition(assessment, subject);
      if (!composition || !composition.ok) return null;
      if (key === SKINFOLD_FAT_KEY) return composition.bodyFat;
      return key === FAT_MASS_KEY ? composition.fatMass : composition.leanMass;
    }
    default:
      return getValue(assessment, key);
  }
}

/**
 * Linhas da tabela de histórico (tela e PDF): as medidas com valor em alguma
 * das `columns`, agrupadas por região, e por último o grupo "Calculados" com
 * os indicadores que têm valor. Cada medida tem { key, label, unit, decimals };
 * o valor de cada célula sai de `getMeasureValue(list, coluna, key, subject)`.
 */
export function historyGroups(list, columns, subject) {
  const measures = keysWithData(columns).map(getBodyMeasure).filter(Boolean);
  const groups = BODY_MEASURE_GROUPS
    .map((group) => ({ ...group, measures: measures.filter((measure) => measure.group === group.key) }))
    .filter((group) => group.measures.length > 0);

  const computed = COMPUTED_MEASURES.filter((info) => (columns || []).some((item) => getMeasureValue(list, item, info.key, subject) !== null));
  if (computed.length > 0) groups.push({ key: 'computed', label: 'Calculados', measures: computed });
  return groups;
}

/**
 * Série de uma medida ao longo das avaliações, da mais antiga para a mais
 * recente, só com as avaliações em que ela foi registrada.
 */
export function measureSeries(list, key, subject) {
  return sortByDateAsc(list)
    .map((assessment) => ({ assessment, value: getMeasureValue(list, assessment, key, subject) }))
    .filter((point) => point.value !== null);
}

/** Valida um valor digitado: vazio, ou número dentro da faixa plausível da medida. */
export function validateInput(key, raw) {
  const measure = getBodyMeasure(key);
  if (!measure) return 'Medida desconhecida';
  if (raw === '' || raw === null || raw === undefined) return null;
  const number = parseInput(raw);
  if (number === null || number <= 0) return 'Valor inválido';
  if (number < measure.min || number > measure.max) {
    const decimals = Number.isInteger(measure.min) ? 0 : 1;
    return `Fora da faixa (${formatNumber(measure.min, decimals)} a ${formatNumber(measure.max, 0)} ${measure.unit})`;
  }
  return null;
}

export function parseInput(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const number = Number(String(raw).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
}
